import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Route, Timer } from "lucide-react";
import { MapPicker } from "@/components/MapPicker";
import { PlaceSearch } from "@/components/customer/PlaceSearch";
import { NearbyTaxis } from "@/components/customer/NearbyTaxis";
import { labelFor } from "@/lib/useRoute";
import { POM_CENTER } from "@/constants/places";
import { ActiveRide } from "@/components/customer/ActiveRide";
import { UpcomingRides, WhenPicker } from "@/components/customer/Scheduling";
import { ExpiredNotice, dismissedExpired } from "@/components/customer/ExpiredNotice";
import { api, errMsg, kina } from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { posterPlace, clearPosterPlace } from "@/lib/poster";

const ACTIVE = ["requested", "accepted", "arrived", "in_progress"];

export const BookRide = () => {
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [mode, setMode] = useState("pickup");
  const [quote, setQuote] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(undefined);
  const [upcoming, setUpcoming] = useState([]);
  const [expired, setExpired] = useState(null);
  const [later, setLater] = useState(false);
  const [at, setAt] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [chosen, setChosen] = useState(null);
  const center = pickup || { lat: POM_CENTER[0], lng: POM_CENTER[1] };

  usePoll(
    () => api.get("/drivers/nearby", { params: { lat: center.lat, lng: center.lng } }).then((r) => setDrivers(r.data)).catch(() => {}),
    8000,
    [center.lat, center.lng]
  );

  const loadActive = useCallback(async () => {
    const { data } = await api.get("/bookings/mine");
    setUpcoming(data.filter((b) => b.status === "scheduled").reverse());
    const lastExpired = data.find((b) => b.status === "expired");
    const recent = lastExpired && Date.now() - new Date(lastExpired.expired_at || lastExpired.created_at) < 86400000;
    setExpired(recent && lastExpired.id !== dismissedExpired() && data[0]?.id === lastExpired.id ? lastExpired : null);
    setActive(data.find((b) => ACTIVE.includes(b.status)) || (data[0]?.status === "completed" && active?.id === data[0].id ? data[0] : null));
  }, [active?.id]);

  usePoll(() => loadActive().catch(() => {}), 5000, [loadActive]);

  useEffect(() => {
    if (!pickup || !dropoff) {
      setQuote(null);
      return;
    }
    api.post("/fare/estimate", { pickup, dropoff }).then((r) => setQuote(r.data)).catch(() => {});
  }, [pickup, dropoff]);

  const onMap = async (lat, lng) => {
    const p = { lat, lng, address: "Locating address…" };
    const setter = mode === "pickup" ? setPickup : setDropoff;
    setter(p);
    if (mode === "pickup") setMode("dropoff");
    const address = await labelFor(lat, lng);
    setter({ lat, lng, address });
  };

  const book = async () => {
    if (later && !at) {
      toast.error("Choose a pickup date and time");
      return;
    }
    setBusy(true);
    try {
      const scheduled_for = later ? new Date(at).toISOString() : null;
      const { data } = await api.post("/bookings", { pickup, dropoff, notes, scheduled_for, requested_driver_id: later ? null : chosen?.driver_id || null, poster_place: posterPlace() });
      clearPosterPlace();
      if (later) {
        toast.success("Ride scheduled — we'll find your driver before pickup");
        setPickup(null);
        setDropoff(null);
        setNotes("");
        setAt("");
        setLater(false);
        loadActive();
      } else {
        setActive(data);
        toast.success("Ride requested — finding a Comfort Taxi near you");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (active === undefined) return <div className="h-96 animate-pulse rounded-2xl bg-white" />;
  if (active) return <ActiveRide ride={active} onDone={() => { setActive(null); setPickup(null); setDropoff(null); }} onChange={loadActive} />;

  return (
    <>
    {expired && (
      <ExpiredNotice
        ride={expired}
        onDismiss={() => setExpired(null)}
        onRebook={(r) => { setPickup(r.pickup); setDropoff(r.dropoff); setNotes(r.notes || ""); setExpired(null); }}
      />
    )}
    <UpcomingRides rides={upcoming} onChange={loadActive} />
    <div className="grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <MapPicker
          pickup={pickup}
          dropoff={dropoff}
          onPick={onMap}
          route={quote?.geometry}
          drivers={later ? [] : drivers}
          selectedDriverId={chosen?.driver_id}
          onDriverClick={(d) => setChosen((c) => (c?.driver_id === d.driver_id ? null : d))}
          fitKey={quote?.geometry?.length || 0}
          className="h-[360px] sm:h-[520px]"
        />
        <p className="mt-2 text-xs text-slate-500">Tip: search a place, use your location, or tap the map to drop the {mode === "pickup" ? "pickup (A)" : "drop-off (B)"} pin. Tap a yellow taxi to request that driver.</p>
      </div>
      <div className="space-y-4 lg:col-span-5">
        <PlaceSearch label="Pickup" value={pickup} onChange={setPickup} active={mode === "pickup"} onActivate={() => setMode("pickup")} color="bg-blue-700" testId="customer-pickup" allowMyLocation />
        <PlaceSearch label="Drop-off" value={dropoff} onChange={setDropoff} active={mode === "dropoff"} onActivate={() => setMode("dropoff")} color="bg-amber-500" testId="customer-dropoff" />
        <WhenPicker later={later} setLater={setLater} at={at} setAt={setAt} />
        {!later && <NearbyTaxis drivers={drivers} selected={chosen} onSelect={setChosen} />}
        <textarea
          data-testid="customer-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Note for driver (e.g. wait at main gate)"
          className="h-20 w-full resize-none rounded-2xl border border-slate-200 bg-white p-4 text-sm outline-none focus:border-blue-600"
        />
        <div className="rounded-2xl bg-[#0B192C] p-6 text-white" data-testid="fare-estimate-card">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-300">Estimated fare · {quote?.source === "road" ? "Road route" : "Approx."} · Cash</p>
          <p className="mt-2 font-mono-num text-4xl font-semibold" data-testid="fare-estimate-amount">{quote ? kina(quote.fare) : "K —"}</p>
          <div className="mt-4 flex gap-6 text-sm text-blue-100">
            <span className="flex items-center gap-1.5"><Route className="h-4 w-4 text-amber-400" />{quote ? `${quote.distance_km} km` : "—"}</span>
            <span className="flex items-center gap-1.5"><Timer className="h-4 w-4 text-amber-400" />{quote ? `~${quote.eta_min} min` : "—"}</span>
            <span className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-amber-400" />Cash</span>
          </div>
          <p className="mt-3 text-xs text-blue-200">Final fare is metered from the GPS distance actually driven.</p>
          {chosen && !later && <p data-testid="chosen-taxi-note" className="mt-2 text-xs text-amber-300">Requesting taxi #{chosen.taxi_no} ({chosen.name}) first</p>}
          <button
            data-testid="book-ride-button"
            disabled={!quote || busy}
            onClick={book}
            className="mt-6 h-12 w-full rounded-xl bg-amber-400 font-bold text-slate-900 transition-all hover:bg-amber-300 active:scale-[0.98] disabled:opacity-40"
          >
            {busy ? "Requesting…" : later ? "Schedule Comfort Taxi" : "Request Comfort Taxi"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};
